import { addNamespace } from './utils'
import LevelsApi from '@/apis/levels'

const levelsApi = new LevelsApi()

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{levels: Array, levelIntervals: Array, errors: Array}} LevelsStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const LevelsStoreTypes = {
  getters: {
    GET_ALL: 'getAll',
    GET_BY_ID: 'getById',
    GET_ERRORS: 'getErrors',
    GET_LEVEL_INTERVALS: 'getLevelIntervals',
  },
  actions: {
    FETCH_ALL: 'fetchAll',
    FETCH_LEVEL_INTERVALS: 'fetchLevelIntervals',
  },
  mutations: {
    ADD_ERROR: 'addError',
    SET_LEVELS: 'setLevels',
    SET_LEVEL_INTERVALS: 'setLevelIntervals',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const LevelsStoreNamespacedTypes = addNamespace(
  'levels',
  LevelsStoreTypes
)

/**
 * @returns {LevelsStoreState}
 */
export function state() {
  return { levels: [], levelIntervals: [], errors: [] }
}

export const getters = {
  /**
   * @param {LevelsStoreState} state
   * @returns {function() : Array}
   */
  [LevelsStoreTypes.getters.GET_ALL]: state => () => {
    return state.levels
  },

  /**
   * @param {number} id
   * @param {LevelsStoreState} state
   * @returns {function() : Array}
   */
  [LevelsStoreTypes.getters.GET_BY_ID]: state => id => {
    return state.levels[id]
  },

  /**
   * @param {LevelsStoreState} state
   * @returns {function() : String}
   */
  [LevelsStoreTypes.getters.GET_ERRORS]: state => () => {
    return state.errors
  },

  /**
   * @param {LevelsStoreState} state
   * @returns {function() : Array}
   */
  [LevelsStoreTypes.getters.GET_LEVEL_INTERVALS]: state => () => {
    return state.levelIntervals
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   */
  [LevelsStoreTypes.actions.FETCH_ALL]({ commit }) {
    levelsApi
      .getAll()
      .then(levels => {
        commit(LevelsStoreTypes.mutations.SET_LEVELS, levels)
      })
      .catch(({ message }) => {
        commit(LevelsStoreTypes.mutations.ADD_ERROR, message)
      })
  },

  /**
   * @param {VuexCommit} commit
   */
  [LevelsStoreTypes.actions.FETCH_LEVEL_INTERVALS]({ commit }) {
    levelsApi
      .getActiveLevelIntervals()
      .then(levelIntervals => {
        commit(LevelsStoreTypes.mutations.SET_LEVEL_INTERVALS, levelIntervals)
      })
      .catch(({ message }) => {
        commit(LevelsStoreTypes.mutations.ADD_ERROR, message)
      })
  },
}

export const mutations = {
  /**
   * @param {LevelsStoreState} state
   * @param {Array} levels
   */
  [LevelsStoreTypes.mutations.SET_LEVELS](state, levels) {
    state.levels = levels
  },

  /**
   * @param {LevelsStoreState} state
   * @param {Array} levelIntervals
   */
  [LevelsStoreTypes.mutations.SET_LEVEL_INTERVALS](state, levelIntervals) {
    state.levelIntervals = levelIntervals
  },

  /**
   * @param {LevelsStoreState} state
   * @param {Object} error
   */
  [LevelsStoreTypes.mutations.ADD_ERROR](state, error) {
    state.errors.push(error)
  },
}

export default {
  namespaced: true,
  Types: LevelsStoreTypes,
  NamespacedTypes: LevelsStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
